import { molarMass } from './formula';
import { makeRng, newSeed, pick, randFloat, randInt, type Rng } from './random';
import type { GeneratedProblem, Generator } from './problem';

/**
 * The built-in problem library, covering the General Chemistry I scope the spec
 * lists. Every one of these is code, not data, and every answer is computed
 * from the sheet's printed atomic masses.
 *
 * Electron configurations and quantum numbers are deliberately absent: they are
 * question-style items, not numeric templates.
 */

const fmt = (value: number, figures: number): string => {
  const exponent = Math.floor(Math.log10(Math.abs(value) || 1));
  const places = Math.max(0, figures - 1 - exponent);
  return value.toFixed(places);
};

// Formulas used across several generators, all in the Gen Chem I vocabulary.
const SIMPLE_COMPOUNDS = ['H2O', 'CO2', 'NaCl', 'C6H12O6', 'NH3', 'CaCO3', 'H2SO4', 'KMnO4', 'Fe2O3', 'MgCl2'] as const;
const IONIC = ['NaCl', 'KBr', 'CaCl2', 'MgSO4', 'Al2(SO4)3', 'Na2CO3', 'KNO3'] as const;

export const GENERATORS: Generator[] = [
  // --------------------------------------------------- unit conversions
  {
    id: 'gen-unit-metric',
    label: 'Metric conversions',
    category: 'Unit Conversions',
    difficulty: 1,
    make: (rng) => {
      const steps = [
        { from: 'km', to: 'm', factor: 1000 },
        { from: 'g', to: 'mg', factor: 1000 },
        { from: 'L', to: 'mL', factor: 1000 },
        { from: 'm', to: 'cm', factor: 100 },
        { from: 'kg', to: 'g', factor: 1000 },
      ];
      const unit = pick(rng, steps);
      const amount = randFloat(rng, 1.5, 95, 3);
      const answer = amount.value * unit.factor;
      return {
        category: 'Unit Conversions',
        difficulty: 1,
        prompt: `Convert ${amount.text} ${unit.from} to ${unit.to}.`,
        answerValue: answer,
        answerUnits: unit.to,
        unitAliases: [unit.to],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: amount.text }, { text: String(unit.factor), exact: true }],
        tolerance: { relative: 0.01 },
        steps: [
          `There are ${unit.factor} ${unit.to} in 1 ${unit.from}, an exact definition, so it does not limit the figures.`,
          `${amount.text} ${unit.from} × ${unit.factor} ${unit.to}/${unit.from} = ${fmt(answer, 3)} ${unit.to}.`,
        ],
      };
    },
  },
  {
    id: 'gen-unit-dimensional',
    label: 'Dimensional analysis',
    category: 'Unit Conversions',
    difficulty: 2,
    make: (rng) => {
      const speed = randFloat(rng, 15, 90, 3);
      const answer = (speed.value * 1000) / 3600;
      return {
        category: 'Unit Conversions',
        difficulty: 2,
        prompt: `A car travels at ${speed.text} km/h. What is that in m/s?`,
        answerValue: answer,
        answerUnits: 'm/s',
        unitAliases: ['m/s', 'ms^-1', 'm s^-1'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: speed.text }],
        tolerance: { relative: 0.01 },
        steps: [
          'Chain the conversions so the units you do not want cancel.',
          `${speed.text} km/h × 1000 m/km × 1 h/3600 s = ${fmt(answer, 3)} m/s.`,
          'Both conversion factors are exact, so the answer keeps the figures of the speed.',
        ],
      };
    },
  },
  // ------------------------------------------------------------ density
  {
    id: 'gen-density',
    label: 'Density',
    category: 'Density',
    difficulty: 1,
    make: (rng) => {
      const mass = randFloat(rng, 8, 240, 3);
      const volume = randFloat(rng, 2, 60, 3);
      const answer = mass.value / volume.value;
      return {
        category: 'Density',
        difficulty: 1,
        prompt: `A sample has a mass of ${mass.text} g and a volume of ${volume.text} mL. What is its density?`,
        answerValue: answer,
        answerUnits: 'g/mL',
        unitAliases: ['g/mL', 'g/cm3', 'g cm^-3', 'g/cm^3'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: mass.text }, { text: volume.text }],
        tolerance: { relative: 0.01 },
        steps: [
          'Density is mass divided by volume.',
          `${mass.text} g ÷ ${volume.text} mL = ${fmt(answer, 3)} g/mL.`,
        ],
      };
    },
  },
  {
    id: 'gen-density-volume',
    label: 'Density to volume',
    category: 'Density',
    difficulty: 2,
    make: (rng) => {
      const density = randFloat(rng, 0.7, 13.6, 3);
      const mass = randFloat(rng, 10, 400, 3);
      const answer = mass.value / density.value;
      return {
        category: 'Density',
        difficulty: 2,
        prompt: `A liquid has a density of ${density.text} g/mL. What volume would ${mass.text} g of it occupy?`,
        answerValue: answer,
        answerUnits: 'mL',
        unitAliases: ['mL', 'cm3', 'cm^3'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: density.text }, { text: mass.text }],
        tolerance: { relative: 0.01 },
        steps: [
          'Rearrange density = mass ÷ volume to volume = mass ÷ density.',
          `${mass.text} g ÷ ${density.text} g/mL = ${fmt(answer, 3)} mL.`,
        ],
      };
    },
  },
  // -------------------------------------------- isotopes and atomic mass
  {
    id: 'gen-average-atomic-mass',
    label: 'Average atomic mass',
    category: 'Atomic Structure',
    difficulty: 2,
    make: (rng) => {
      const massA = randFloat(rng, 9, 80, 4);
      const gap = randFloat(rng, 1.5, 4, 2);
      const percentA = randFloat(rng, 15, 85, 3);
      const massB = massA.value + gap.value;
      const fractionA = percentA.value / 100;
      const answer = fractionA * massA.value + (1 - fractionA) * massB;
      return {
        category: 'Atomic Structure',
        difficulty: 2,
        prompt: `An element has two isotopes: one of mass ${massA.text} amu at ${percentA.text}% abundance, and one of mass ${fmt(massB, 4)} amu making up the rest. What is its average atomic mass?`,
        answerValue: answer,
        answerUnits: 'amu',
        unitAliases: ['amu', 'u', 'g/mol'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: massA.text }, { text: percentA.text }],
        tolerance: { relative: 0.01 },
        steps: [
          `The second isotope makes up 100 − ${percentA.text} = ${fmt(100 - percentA.value, 3)}%.`,
          'Turn each percentage into a decimal fraction and weight each mass by it.',
          `(${(fractionA).toFixed(4)} × ${massA.text}) + (${(1 - fractionA).toFixed(4)} × ${fmt(massB, 4)}) = ${fmt(answer, 4)} amu.`,
        ],
      };
    },
  },
  {
    id: 'gen-isotope-particles',
    label: 'Protons, neutrons and electrons',
    category: 'Atomic Structure',
    difficulty: 1,
    make: (rng) => {
      const protons = randInt(rng, 5, 40);
      const neutrons = protons + randInt(rng, 0, 8);
      const charge = pick(rng, [0, 0, 1, 2, -1, -2]);
      const electrons = protons - charge;
      const label = charge === 0 ? 'a neutral atom' : `an ion with a ${charge > 0 ? '+' : ''}${charge} charge`;
      return {
        category: 'Atomic Structure',
        difficulty: 1,
        prompt: `${label[0]!.toUpperCase()}${label.slice(1)} has ${protons} protons and a mass number of ${protons + neutrons}. How many electrons does it have?`,
        answerValue: electrons,
        answerUnits: '',
        unitAliases: [],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: String(protons), exact: true }],
        tolerance: { absolute: 0.5 },
        steps: [
          'The mass number counts protons plus neutrons, so it says nothing about electrons.',
          charge === 0
            ? 'A neutral atom has as many electrons as protons.'
            : `A ${charge > 0 ? 'positive' : 'negative'} charge means ${Math.abs(charge)} ${Math.abs(charge) === 1 ? 'electron' : 'electrons'} ${charge > 0 ? 'fewer than' : 'more than'} the protons.`,
          charge === 0
            ? `So it has ${electrons} electrons.`
            : `${protons} ${charge > 0 ? '−' : '+'} ${Math.abs(charge)} = ${electrons} electrons.`,
        ],
      };
    },
  },
  // ---------------------------------------------------- moles and molar mass
  {
    id: 'gen-molar-mass',
    label: 'Molar mass',
    category: 'Moles & Molar Mass',
    difficulty: 1,
    make: (rng) => {
      const formula = pick(rng, SIMPLE_COMPOUNDS);
      const answer = molarMass(formula);
      return {
        category: 'Moles & Molar Mass',
        difficulty: 1,
        prompt: `What is the molar mass of ${formula}?`,
        answerValue: answer,
        answerUnits: 'g/mol',
        unitAliases: ['g/mol', 'g mol^-1', 'amu', 'u'],
        sigfigRule: 4,
        ruleInputs: [{ text: fmt(answer, 5), exact: true }],
        tolerance: { relative: 0.01 },
        steps: [
          'Add up the atomic mass of every atom in the formula.',
          `Use the masses printed on your table. ${formula} works out to ${fmt(answer, 5)} g/mol.`,
        ],
      };
    },
  },
  {
    id: 'gen-mass-to-moles',
    label: 'Mass to moles',
    category: 'Moles & Molar Mass',
    difficulty: 1,
    make: (rng) => {
      const formula = pick(rng, SIMPLE_COMPOUNDS);
      const mass = randFloat(rng, 5, 250, 3);
      const molar = molarMass(formula);
      const answer = mass.value / molar;
      return {
        category: 'Moles & Molar Mass',
        difficulty: 1,
        prompt: `How many moles are in ${mass.text} g of ${formula}?`,
        answerValue: answer,
        answerUnits: 'mol',
        unitAliases: ['mol', 'moles', 'mole'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: mass.text }, { text: fmt(molar, 5), exact: true }],
        tolerance: { relative: 0.01 },
        steps: [
          `The molar mass of ${formula} is ${fmt(molar, 5)} g/mol.`,
          `${mass.text} g ÷ ${fmt(molar, 5)} g/mol = ${fmt(answer, 3)} mol.`,
        ],
      };
    },
  },
  {
    id: 'gen-moles-to-particles',
    label: 'Moles to particles',
    category: 'Moles & Molar Mass',
    difficulty: 2,
    make: (rng) => {
      const moles = randFloat(rng, 0.15, 6, 3);
      const answer = moles.value * 6.022e23;
      return {
        category: 'Moles & Molar Mass',
        difficulty: 2,
        prompt: `How many molecules are in ${moles.text} mol of a substance?`,
        answerValue: answer,
        answerUnits: 'molecules',
        unitAliases: ['molecules', 'particles', 'atoms', ''],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: moles.text }, { text: '6.022e23' }],
        tolerance: { relative: 0.01 },
        steps: [
          "Avogadro's number is 6.022e23 particles per mole.",
          `${moles.text} mol × 6.022e23 /mol = ${answer.toExponential(2)} molecules.`,
        ],
      };
    },
  },
  // ------------------------------------------------- percent composition
  {
    id: 'gen-percent-composition',
    label: 'Percent composition',
    category: 'Percent Composition',
    difficulty: 2,
    make: (rng) => {
      const formula = pick(rng, ['H2O', 'CO2', 'CaCO3', 'NaCl', 'C6H12O6', 'Fe2O3'] as const);
      const element = pick(rng, ['O', 'C', 'H'] as const);
      const counts: Record<string, Record<string, number>> = {
        H2O: { H: 2, O: 1 },
        CO2: { C: 1, O: 2 },
        CaCO3: { Ca: 1, C: 1, O: 3 },
        NaCl: { Na: 1, Cl: 1 },
        C6H12O6: { C: 6, H: 12, O: 6 },
        Fe2O3: { Fe: 2, O: 3 },
      };
      const present = counts[formula]!;
      const symbol = present[element] ? element : (Object.keys(present)[0] as string);
      const n = present[symbol]!;
      const total = molarMass(formula);
      const part = molarMass(symbol) * n;
      const answer = (part / total) * 100;
      return {
        category: 'Percent Composition',
        difficulty: 2,
        prompt: `What percent by mass of ${formula} is ${symbol}?`,
        answerValue: answer,
        answerUnits: '%',
        unitAliases: ['%', 'percent', ''],
        sigfigRule: 3,
        ruleInputs: [{ text: fmt(total, 5), exact: true }],
        tolerance: { relative: 0.01 },
        steps: [
          `${formula} has ${n} ${symbol} per formula unit, contributing ${fmt(part, 5)} g/mol.`,
          `Its total molar mass is ${fmt(total, 5)} g/mol.`,
          `${fmt(part, 5)} ÷ ${fmt(total, 5)} × 100 = ${fmt(answer, 3)}%.`,
        ],
      };
    },
  },
  {
    id: 'gen-empirical-formula',
    label: 'Empirical formula from percentages',
    category: 'Empirical Formulas',
    difficulty: 3,
    make: (rng) => {
      // Built backwards from a known formula so the answer is always clean.
      const target = pick(rng, [
        { formula: 'CH2O', c: 1, h: 2, o: 1 },
        { formula: 'C2H4O2', c: 2, h: 4, o: 2 },
        { formula: 'C3H6O3', c: 3, h: 6, o: 3 },
      ] as const);
      const mass = molarMass(target.formula);
      const multiplier = randInt(rng, 2, 6);
      const molecular = mass * multiplier;
      return {
        category: 'Empirical Formulas',
        difficulty: 3,
        prompt: `A compound has the empirical formula ${target.formula} and a molar mass of ${fmt(molecular, 4)} g/mol. What is the multiplier that turns the empirical formula into the molecular formula?`,
        answerValue: multiplier,
        answerUnits: '',
        unitAliases: [],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: fmt(molecular, 4), exact: true }],
        tolerance: { absolute: 0.1 },
        steps: [
          `The empirical formula mass of ${target.formula} is ${fmt(mass, 4)} g/mol.`,
          `Divide the molar mass by it: ${fmt(molecular, 4)} ÷ ${fmt(mass, 4)} = ${multiplier}.`,
          'The multiplier is always a whole number; if it is not, check the arithmetic.',
        ],
      };
    },
  },
  // ------------------------------------------------------- stoichiometry
  {
    id: 'gen-stoich-mass-to-mass',
    label: 'Mass to mass',
    category: 'Stoichiometry',
    difficulty: 2,
    make: (rng) => {
      const reaction = pick(rng, [
        { eq: '2 H2 + O2 → 2 H2O', from: 'H2', to: 'H2O', ratio: 1 },
        { eq: 'N2 + 3 H2 → 2 NH3', from: 'N2', to: 'NH3', ratio: 2 },
        { eq: 'CH4 + 2 O2 → CO2 + 2 H2O', from: 'CH4', to: 'CO2', ratio: 1 },
        { eq: '2 Na + Cl2 → 2 NaCl', from: 'Na', to: 'NaCl', ratio: 1 },
      ] as const);
      const mass = randFloat(rng, 5, 90, 3);
      const fromMolar = molarMass(reaction.from);
      const toMolar = molarMass(reaction.to);
      const answer = (mass.value / fromMolar) * reaction.ratio * toMolar;
      return {
        category: 'Stoichiometry',
        difficulty: 2,
        prompt: `For ${reaction.eq}, how many grams of ${reaction.to} form from ${mass.text} g of ${reaction.from}, with the other reactant in excess?`,
        answerValue: answer,
        answerUnits: 'g',
        unitAliases: ['g', 'grams', 'gram'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: mass.text }, { text: fmt(fromMolar, 5), exact: true }],
        tolerance: { relative: 0.01 },
        steps: [
          `Convert to moles: ${mass.text} g ÷ ${fmt(fromMolar, 5)} g/mol = ${fmt(mass.value / fromMolar, 4)} mol ${reaction.from}.`,
          `Apply the mole ratio: ${reaction.ratio} mol ${reaction.to} per mol ${reaction.from}.`,
          `Convert back to grams with ${fmt(toMolar, 5)} g/mol: ${fmt(answer, 3)} g ${reaction.to}.`,
        ],
      };
    },
  },
  {
    id: 'gen-limiting-reagent',
    label: 'Limiting reagent',
    category: 'Stoichiometry',
    difficulty: 3,
    make: (rng) => {
      const molesN2 = randFloat(rng, 1, 5, 2);
      const molesH2 = randFloat(rng, 1, 9, 2);
      const byN2 = molesN2.value / 1;
      const byH2 = molesH2.value / 3;
      const limiting = byN2 <= byH2 ? 'N2' : 'H2';
      const answer = Math.min(byN2, byH2) * 2;
      return {
        category: 'Stoichiometry',
        difficulty: 3,
        prompt: `For N2 + 3 H2 → 2 NH3 you start with ${molesN2.text} mol N2 and ${molesH2.text} mol H2. How many moles of NH3 can form?`,
        answerValue: answer,
        answerUnits: 'mol',
        unitAliases: ['mol', 'moles'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: molesN2.text }, { text: molesH2.text }],
        tolerance: { relative: 0.01 },
        steps: [
          `Divide each amount by its coefficient: N2 gives ${fmt(byN2, 3)}, H2 gives ${fmt(byH2, 3)}.`,
          `The smaller one limits the reaction, so ${limiting} runs out first.`,
          `Multiply by the NH3 coefficient: ${fmt(Math.min(byN2, byH2), 3)} × 2 = ${fmt(answer, 3)} mol NH3.`,
        ],
      };
    },
  },
  {
    id: 'gen-percent-yield',
    label: 'Percent yield',
    category: 'Percent Yield',
    difficulty: 2,
    make: (rng) => {
      const theoretical = randFloat(rng, 8, 90, 3);
      const fraction = randFloat(rng, 0.55, 0.96, 2);
      const actual = theoretical.value * fraction.value;
      const answer = (actual / theoretical.value) * 100;
      return {
        category: 'Percent Yield',
        difficulty: 2,
        prompt: `A reaction should give ${theoretical.text} g of product, and you isolate ${fmt(actual, 3)} g. What is the percent yield?`,
        answerValue: answer,
        answerUnits: '%',
        unitAliases: ['%', 'percent', ''],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: theoretical.text }, { text: fmt(actual, 3) }],
        tolerance: { relative: 0.01 },
        steps: [
          'Percent yield is the actual yield over the theoretical yield, times 100.',
          `${fmt(actual, 3)} ÷ ${theoretical.text} × 100 = ${fmt(answer, 3)}%.`,
        ],
      };
    },
  },
  // ----------------------------------------------------------- solutions
  {
    id: 'gen-molarity',
    label: 'Molarity',
    category: 'Solutions',
    difficulty: 1,
    make: (rng) => {
      const formula = pick(rng, IONIC);
      const mass = randFloat(rng, 2, 60, 3);
      const volumeML = randFloat(rng, 100, 900, 3);
      const molar = molarMass(formula);
      const answer = mass.value / molar / (volumeML.value / 1000);
      return {
        category: 'Solutions',
        difficulty: 1,
        prompt: `What is the molarity of a solution made by dissolving ${mass.text} g of ${formula} in enough water to make ${volumeML.text} mL of solution?`,
        answerValue: answer,
        answerUnits: 'M',
        unitAliases: ['M', 'mol/L', 'molar', 'mol L^-1'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: mass.text }, { text: volumeML.text }],
        tolerance: { relative: 0.01 },
        steps: [
          `Moles first: ${mass.text} g ÷ ${fmt(molar, 5)} g/mol = ${fmt(mass.value / molar, 4)} mol.`,
          `Volume in litres: ${volumeML.text} mL = ${fmt(volumeML.value / 1000, 4)} L.`,
          `M = mol ÷ L = ${fmt(answer, 3)} M.`,
        ],
      };
    },
  },
  {
    id: 'gen-dilution',
    label: 'Dilution',
    category: 'Solutions',
    difficulty: 2,
    make: (rng) => {
      const m1 = randFloat(rng, 0.5, 6, 3);
      const v1 = randFloat(rng, 10, 90, 3);
      const v2 = randFloat(rng, 120, 900, 3);
      const answer = (m1.value * v1.value) / v2.value;
      return {
        category: 'Solutions',
        difficulty: 2,
        prompt: `You dilute ${v1.text} mL of ${m1.text} M solution to a final volume of ${v2.text} mL. What is the new concentration?`,
        answerValue: answer,
        answerUnits: 'M',
        unitAliases: ['M', 'mol/L', 'molar'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: m1.text }, { text: v1.text }, { text: v2.text }],
        tolerance: { relative: 0.01 },
        steps: [
          'Diluting changes the volume but not the moles of solute, so M1V1 = M2V2.',
          `M2 = (${m1.text} × ${v1.text}) ÷ ${v2.text} = ${fmt(answer, 3)} M.`,
          'The volumes can stay in mL as long as both are in the same unit.',
        ],
      };
    },
  },
  {
    id: 'gen-solution-stoich',
    label: 'Solution stoichiometry',
    category: 'Solutions',
    difficulty: 3,
    make: (rng) => {
      const molarity = randFloat(rng, 0.1, 2.5, 3);
      const volumeML = randFloat(rng, 15, 250, 3);
      const answer = molarity.value * (volumeML.value / 1000);
      return {
        category: 'Solutions',
        difficulty: 3,
        prompt: `How many moles of solute are in ${volumeML.text} mL of a ${molarity.text} M solution?`,
        answerValue: answer,
        answerUnits: 'mol',
        unitAliases: ['mol', 'moles'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: molarity.text }, { text: volumeML.text }],
        tolerance: { relative: 0.01 },
        steps: [
          `Convert the volume: ${volumeML.text} mL = ${fmt(volumeML.value / 1000, 4)} L.`,
          `Moles = M × L = ${molarity.text} × ${fmt(volumeML.value / 1000, 4)} = ${fmt(answer, 3)} mol.`,
        ],
      };
    },
  },
  // ------------------------------------------------------------ gas laws
  {
    id: 'gen-boyle',
    label: "Boyle's law",
    category: 'Gas Laws',
    difficulty: 1,
    make: (rng) => {
      const p1 = randFloat(rng, 0.8, 4, 3);
      const v1 = randFloat(rng, 1, 12, 3);
      const p2 = randFloat(rng, 0.8, 6, 3);
      const answer = (p1.value * v1.value) / p2.value;
      return {
        category: 'Gas Laws',
        difficulty: 1,
        prompt: `A gas occupies ${v1.text} L at ${p1.text} atm. What volume does it occupy at ${p2.text} atm, at constant temperature?`,
        answerValue: answer,
        answerUnits: 'L',
        unitAliases: ['L', 'litres', 'liters'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: p1.text }, { text: v1.text }, { text: p2.text }],
        tolerance: { relative: 0.01 },
        steps: [
          'At constant temperature P1V1 = P2V2.',
          `V2 = (${p1.text} × ${v1.text}) ÷ ${p2.text} = ${fmt(answer, 3)} L.`,
        ],
      };
    },
  },
  {
    id: 'gen-charles',
    label: "Charles's law",
    category: 'Gas Laws',
    difficulty: 2,
    make: (rng) => {
      const v1 = randFloat(rng, 1, 10, 3);
      const t1C = randInt(rng, 5, 60);
      const t2C = randInt(rng, 70, 250);
      const t1 = t1C + 273.15;
      const t2 = t2C + 273.15;
      const answer = (v1.value * t2) / t1;
      return {
        category: 'Gas Laws',
        difficulty: 2,
        prompt: `A gas occupies ${v1.text} L at ${t1C} °C. What volume does it occupy at ${t2C} °C, at constant pressure?`,
        answerValue: answer,
        answerUnits: 'L',
        unitAliases: ['L', 'litres', 'liters'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: v1.text }],
        tolerance: { relative: 0.01 },
        steps: [
          `Temperatures must be absolute, so add 273.15: ${t1C} °C = ${t1.toFixed(2)} K and ${t2C} °C = ${t2.toFixed(2)} K.`,
          'At constant pressure V1/T1 = V2/T2.',
          `V2 = ${v1.text} × ${t2.toFixed(2)} ÷ ${t1.toFixed(2)} = ${fmt(answer, 3)} L.`,
        ],
      };
    },
  },
  {
    id: 'gen-gay-lussac',
    label: "Gay-Lussac's law",
    category: 'Gas Laws',
    difficulty: 2,
    make: (rng) => {
      const p1 = randFloat(rng, 1, 5, 3);
      const t1C = randInt(rng, 10, 60);
      const t2C = randInt(rng, 80, 300);
      const t1 = t1C + 273.15;
      const t2 = t2C + 273.15;
      const answer = (p1.value * t2) / t1;
      return {
        category: 'Gas Laws',
        difficulty: 2,
        prompt: `A sealed container holds gas at ${p1.text} atm and ${t1C} °C. What is the pressure at ${t2C} °C?`,
        answerValue: answer,
        answerUnits: 'atm',
        unitAliases: ['atm', 'atmospheres'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: p1.text }],
        tolerance: { relative: 0.01 },
        steps: [
          `Convert to kelvin: ${t1.toFixed(2)} K and ${t2.toFixed(2)} K.`,
          'At constant volume P1/T1 = P2/T2.',
          `P2 = ${p1.text} × ${t2.toFixed(2)} ÷ ${t1.toFixed(2)} = ${fmt(answer, 3)} atm.`,
        ],
      };
    },
  },
  {
    id: 'gen-combined-gas',
    label: 'Combined gas law',
    category: 'Gas Laws',
    difficulty: 3,
    make: (rng) => {
      const p1 = randFloat(rng, 1, 3, 3);
      const v1 = randFloat(rng, 2, 10, 3);
      const t1 = randInt(rng, 250, 320);
      const p2 = randFloat(rng, 1, 4, 3);
      const t2 = randInt(rng, 330, 500);
      const answer = (p1.value * v1.value * t2) / (t1 * p2.value);
      return {
        category: 'Gas Laws',
        difficulty: 3,
        prompt: `A gas at ${p1.text} atm occupies ${v1.text} L at ${t1} K. What volume does it occupy at ${p2.text} atm and ${t2} K?`,
        answerValue: answer,
        answerUnits: 'L',
        unitAliases: ['L', 'litres', 'liters'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: p1.text }, { text: v1.text }, { text: p2.text }],
        tolerance: { relative: 0.01 },
        steps: [
          'P1V1/T1 = P2V2/T2, with both temperatures already in kelvin.',
          `V2 = (${p1.text} × ${v1.text} × ${t2}) ÷ (${t1} × ${p2.text}) = ${fmt(answer, 3)} L.`,
        ],
      };
    },
  },
  {
    id: 'gen-ideal-gas',
    label: 'Ideal gas law',
    category: 'Gas Laws',
    difficulty: 2,
    make: (rng) => {
      const moles = randFloat(rng, 0.2, 4, 3);
      const tempK = randInt(rng, 260, 480);
      const volume = randFloat(rng, 2, 40, 3);
      const answer = (moles.value * 0.08206 * tempK) / volume.value;
      return {
        category: 'Gas Laws',
        difficulty: 2,
        prompt: `What pressure does ${moles.text} mol of an ideal gas exert in a ${volume.text} L container at ${tempK} K?`,
        answerValue: answer,
        answerUnits: 'atm',
        unitAliases: ['atm', 'atmospheres'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: moles.text }, { text: volume.text }, { text: '0.08206' }],
        tolerance: { relative: 0.01 },
        steps: [
          'Rearrange PV = nRT to P = nRT ÷ V.',
          `R is 0.08206 L·atm/(mol·K) when pressure is in atm.`,
          `P = (${moles.text} × 0.08206 × ${tempK}) ÷ ${volume.text} = ${fmt(answer, 3)} atm.`,
        ],
      };
    },
  },
  {
    id: 'gen-partial-pressure',
    label: 'Partial pressures',
    category: 'Gas Laws',
    difficulty: 2,
    make: (rng) => {
      const total = randFloat(rng, 1.5, 5, 3);
      const pA = randFloat(rng, 0.2, 0.8, 2);
      const pB = randFloat(rng, 0.3, 0.9, 2);
      const answer = total.value - pA.value - pB.value;
      return {
        category: 'Gas Laws',
        difficulty: 2,
        prompt: `A mixture of three gases has a total pressure of ${total.text} atm. Two of them contribute ${pA.text} atm and ${pB.text} atm. What is the partial pressure of the third?`,
        answerValue: answer,
        answerUnits: 'atm',
        unitAliases: ['atm', 'atmospheres'],
        sigfigRule: 'decimal-places',
        ruleInputs: [{ text: total.text }, { text: pA.text }, { text: pB.text }],
        tolerance: { relative: 0.02 },
        steps: [
          "Dalton's law: the partial pressures add up to the total.",
          `${total.text} − ${pA.text} − ${pB.text} = ${answer.toFixed(2)} atm.`,
          'This is subtraction, so the answer keeps the fewest decimal places, not significant figures.',
        ],
      };
    },
  },
  {
    id: 'gen-gas-stoich',
    label: 'Gas stoichiometry at STP',
    category: 'Gas Laws',
    difficulty: 3,
    make: (rng) => {
      const mass = randFloat(rng, 2, 40, 3);
      const molar = molarMass('CaCO3');
      const answer = (mass.value / molar) * 22.4;
      return {
        category: 'Gas Laws',
        difficulty: 3,
        prompt: `CaCO3 → CaO + CO2. What volume of CO2 at STP comes from ${mass.text} g of CaCO3?`,
        answerValue: answer,
        answerUnits: 'L',
        unitAliases: ['L', 'litres', 'liters'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: mass.text }, { text: '22.4' }],
        tolerance: { relative: 0.01 },
        steps: [
          `Moles of CaCO3: ${mass.text} g ÷ ${fmt(molar, 5)} g/mol = ${fmt(mass.value / molar, 4)} mol.`,
          'The equation is 1:1, so that is the same number of moles of CO2.',
          `At STP one mole of gas fills 22.4 L: ${fmt(answer, 3)} L.`,
        ],
      };
    },
  },
  // ----------------------------------------------------- thermochemistry
  {
    id: 'gen-heat-capacity',
    label: 'q = mcΔT',
    category: 'Thermochemistry',
    difficulty: 2,
    make: (rng) => {
      const mass = randFloat(rng, 20, 350, 3);
      const deltaT = randFloat(rng, 5, 70, 3);
      const c = 4.184;
      const answer = mass.value * c * deltaT.value;
      return {
        category: 'Thermochemistry',
        difficulty: 2,
        prompt: `How much heat is needed to raise ${mass.text} g of water by ${deltaT.text} °C? The specific heat of water is 4.184 J/(g·°C).`,
        answerValue: answer,
        answerUnits: 'J',
        unitAliases: ['J', 'joules', 'joule'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: mass.text }, { text: deltaT.text }, { text: '4.184' }],
        tolerance: { relative: 0.01 },
        steps: [
          'q = m c ΔT.',
          `q = ${mass.text} g × 4.184 J/(g·°C) × ${deltaT.text} °C = ${fmt(answer, 3)} J.`,
        ],
      };
    },
  },
  {
    id: 'gen-calorimetry',
    label: 'Calorimetry',
    category: 'Thermochemistry',
    difficulty: 3,
    make: (rng) => {
      const mass = randFloat(rng, 50, 250, 3);
      const deltaT = randFloat(rng, 2, 25, 3);
      const moles = randFloat(rng, 0.05, 0.5, 3);
      const heat = mass.value * 4.184 * deltaT.value;
      const answer = -heat / moles.value / 1000;
      return {
        category: 'Thermochemistry',
        difficulty: 3,
        prompt: `Burning ${moles.text} mol of a substance warms ${mass.text} g of water by ${deltaT.text} °C. What is the enthalpy change per mole, in kJ/mol? Give it with its sign.`,
        answerValue: answer,
        answerUnits: 'kJ/mol',
        unitAliases: ['kJ/mol', 'kJ mol^-1', 'kj/mol'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: mass.text }, { text: deltaT.text }, { text: moles.text }],
        tolerance: { relative: 0.02 },
        steps: [
          `Heat absorbed by the water: q = ${mass.text} × 4.184 × ${deltaT.text} = ${fmt(heat, 4)} J.`,
          'The water gained that heat, so the reaction lost it — the sign flips.',
          `Per mole: −${fmt(heat / 1000, 4)} kJ ÷ ${moles.text} mol = ${fmt(answer, 3)} kJ/mol.`,
        ],
      };
    },
  },
  {
    id: 'gen-hess',
    label: "Hess's law",
    category: 'Thermochemistry',
    difficulty: 3,
    make: (rng) => {
      const a = randFloat(rng, -600, -100, 3);
      const b = randFloat(rng, -400, -50, 3);
      const answer = a.value + b.value;
      return {
        category: 'Thermochemistry',
        difficulty: 3,
        prompt: `A reaction can be written as the sum of two steps, with ΔH of ${a.text} kJ and ${b.text} kJ. What is ΔH for the overall reaction?`,
        answerValue: answer,
        answerUnits: 'kJ',
        unitAliases: ['kJ', 'kilojoules'],
        sigfigRule: 'decimal-places',
        ruleInputs: [{ text: a.text }, { text: b.text }],
        tolerance: { relative: 0.01 },
        steps: [
          "Hess's law: if the steps add to the overall reaction, their enthalpies add too.",
          `${a.text} + ${b.text} = ${fmt(answer, 4)} kJ.`,
          'Adding means the answer keeps decimal places, not significant figures.',
        ],
      };
    },
  },
  // ---------------------------------------------------- light and energy
  {
    id: 'gen-wavelength-frequency',
    label: 'Wavelength and frequency',
    category: 'Light & Energy',
    difficulty: 2,
    make: (rng) => {
      const wavelengthNm = randFloat(rng, 200, 750, 3);
      const answer = 2.998e8 / (wavelengthNm.value * 1e-9);
      return {
        category: 'Light & Energy',
        difficulty: 2,
        prompt: `What is the frequency of light with a wavelength of ${wavelengthNm.text} nm?`,
        answerValue: answer,
        answerUnits: 'Hz',
        unitAliases: ['Hz', 's^-1', '/s', 'hertz'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: wavelengthNm.text }, { text: '2.998e8' }],
        tolerance: { relative: 0.01 },
        steps: [
          `Convert to metres: ${wavelengthNm.text} nm = ${(wavelengthNm.value * 1e-9).toExponential(3)} m.`,
          'c = λν, so ν = c ÷ λ with c = 2.998e8 m/s.',
          `ν = ${answer.toExponential(3)} Hz.`,
        ],
      };
    },
  },
  {
    id: 'gen-photon-energy',
    label: 'Photon energy',
    category: 'Light & Energy',
    difficulty: 3,
    make: (rng) => {
      const wavelengthNm = randFloat(rng, 180, 700, 3);
      const answer = (6.626e-34 * 2.998e8) / (wavelengthNm.value * 1e-9);
      return {
        category: 'Light & Energy',
        difficulty: 3,
        prompt: `What is the energy of a single photon of wavelength ${wavelengthNm.text} nm?`,
        answerValue: answer,
        answerUnits: 'J',
        unitAliases: ['J', 'joules'],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: wavelengthNm.text }, { text: '6.626e-34' }],
        tolerance: { relative: 0.01 },
        steps: [
          'E = hc ÷ λ, with h = 6.626e-34 J·s and c = 2.998e8 m/s.',
          `λ = ${(wavelengthNm.value * 1e-9).toExponential(3)} m.`,
          `E = ${answer.toExponential(3)} J.`,
        ],
      };
    },
  },
  // ------------------------------------------------- significant figures
  {
    id: 'gen-sigfig-count',
    label: 'Counting significant figures',
    category: 'Significant Figures',
    difficulty: 1,
    make: (rng) => {
      const sample = pick(rng, [
        { text: '0.00450', count: 3 },
        { text: '1.2300', count: 5 },
        { text: '50600', count: 3 },
        { text: '0.0708', count: 3 },
        { text: '9.00', count: 3 },
        { text: '120.0', count: 4 },
        { text: '6.022e23', count: 4 },
      ] as const);
      return {
        category: 'Significant Figures',
        difficulty: 1,
        prompt: `How many significant figures does ${sample.text} have?`,
        answerValue: sample.count,
        answerUnits: '',
        unitAliases: [],
        sigfigRule: 'min-of-inputs',
        ruleInputs: [{ text: String(sample.count), exact: true }],
        tolerance: { absolute: 0.5 },
        steps: [
          'Leading zeros never count. Zeros between digits always do.',
          'Trailing zeros count only with a decimal point or in scientific notation.',
          `${sample.text} has ${sample.count} significant figures.`,
        ],
      };
    },
  },
];

export const GENERATORS_BY_ID = new Map(GENERATORS.map((g) => [g.id, g]));

export function generatorCategories(): string[] {
  return [...new Set(GENERATORS.map((g) => g.category))].sort();
}

/** Builds one problem. The same seed always gives the same problem. */
export function generate(generatorId: string, seed: string = newSeed()): GeneratedProblem {
  const generator = GENERATORS_BY_ID.get(generatorId);
  if (!generator) throw new Error(`There is no generator called "${generatorId}".`);
  const rng: Rng = makeRng(`${generatorId}:${seed}`);
  return { ...generator.make(rng), sourceId: generatorId, seed };
}
